<template>
  <v-container class="fin-tour ma-0 pa-0">
    <v-btn @click="startTour()"
           dark
           outlined
           large
           class="btn-start-tour"
           v-if="showBtn">
      {{ btnLabel }}
    </v-btn>
    <v-tour ref="finTour"
            :callbacks="myCallbacks"
            :name="tourID"
            :steps="tourModel">
      <template slot-scope="tour">
        <v-step v-if="tour.steps[tour.currentStep]"
                style="min-width: 300px"
                :key="tour.currentStep"
                :step="tour.steps[tour.currentStep]"
                :previous-step="tour.previousStep"
                :next-step="tour.nextStep"
                :stop="tour.stop"
                :skip="tour.skip"
                :is-first="tour.isFirst"
                :is-last="tour.isLast"
                :labels="tour.labels">
          <div class="v-step__header pt-4 px-4 pb-0 mb-0 text-left"
               slot="header">
            <v-progress-linear class="mb-2"
                               :value="((tour.currentStep + 1)/tour.steps.length)*100"
                               color="#fff"/>
            <v-row>
              <v-col class="font-weight-bold">
                {{ tour.steps[tour.currentStep].header }}
              </v-col>
              <v-col :cols="1">
            <span>
              <v-btn x-small
                     @click="stopTour()"
                     icon
                     class="float-right close-btn">
                <v-icon :color="$finColors.primaryLight">
                  icon-close
                </v-icon>
              </v-btn>
            </span>
              </v-col>
            </v-row>
          </div>

          <div slot="content" class="text-left">
            <Content :slice="wrapInSlice(tour.steps[tour.currentStep].content)"/>
          </div>
          <div slot="actions">
            <v-row class="mx-0 mt-2 mb-0 pa-0">
              <v-col class="ma-0 pa-0 text-left d-flex justify-start align-center" cols="2">
                {{ tour.currentStep + 1 }}/{{ tour.steps.length }}
              </v-col>
              <v-col class="ma-0 pa-0 text-right d-flex justify-end align-end">
                <v-btn @click="tour.previousStep" class="mr-2"
                       v-if="!tour.isFirst">Zurück
                </v-btn>
                <v-btn @click="tour.nextStep"
                       v-if="!tour.isLast">
                  Weiter
                </v-btn>
                <v-btn @click="finishTour()"
                       v-if="tour.isLast"
                       text
                       dark>
                  Tour beenden
                </v-btn>

              </v-col>
            </v-row>
          </div>
        </v-step>
      </template>
    </v-tour>
  </v-container>
</template>

<script>
import Content from "@/components/common/lexicon/LexiconOverlay/slices/Content"
import { EventBus } from "@/plugins/fincraft"
import AmplitudePush from "@/mixins/AmplitudePush"

export default {
  name: "finTour",
  components: {
    Content
  },
  mixins: [AmplitudePush],
  props: {
    /**
     * Unique identifier for the tour. Is needed when multiple tours are on the same page
     */
    tourID: {
      type: String,
      required: true
    },
    /**
     * Name of the guided tour
     */
    tourName: {
      type: String,
      required: true,
      default: 'please define tour name in Prismic! This is required to track events to amplitude'
    },
    /**
     * Label of the button that start the tour
     */
    btnLabel: {
      type: String,
      default: () => 'Tour starten'
    },
    /**
     * When true, the tour is started on mount
     */
    autoRun: {
      type: Boolean,
      default: false
    },
    /**
     * Steps of the guided tour
     */
    steps: {
      type: Array,
      default: () => {
        return []
      }
    },
    /**
     * If the button to start the tour shall be shown
     */
    showBtn: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      tourWatchedKey: 'finAutoStartedTour',
      windowWidth: window.innerWidth,
      myCallbacks: {
        onPreviousStep: this.myCustomPreviousStepCallback,
        onNextStep: this.myCustomNextStepCallback,
        onStart: this.myCustomStartCallback,
        onStop: this.myCustomStopCallback,
        onFinish: this.myCustomFinishCallback
        // onSkip: this.myCustomSkipCallback, not needed so far
      }
    }
  },
  computed: {
    watchedToures: {
      get() {
        const storedValue = localStorage.getItem(this.tourWatchedKey) || '{}'
        return JSON.parse(storedValue)
      },
      set(newVal) {
        localStorage.setItem(this.tourWatchedKey, JSON.stringify(newVal))
      }
    },
    tourWatched: {
      get() {
        return this.watchedToures[this.tourID] || false
      },
      set(val) {
        const newVal = {...this.watchedToures}
        newVal[this.tourID] = val
        this.watchedToures = newVal
      }
    },
    tourModel() {
      let result = JSON.parse(JSON.stringify(this.steps))

      result = result.filter(step => {
        if (!document.querySelector(step.target)) {
          return false
        }

        switch (this.$vuetify.breakpoint.name) {
          case "xl":
          case "lg":
            if (step.breakpoint_lg) {
              return true
            }
            break
          case "md":
            if (step.breakpoint_md) {
              return true
            }
            break
          case 'sm':
            if (step.breakpoint_sm) {
              return true
            }
            break
          case 'xs':
            if (step.breakpoint_xs) {
              return true
            }
            break
        }
        return false
      }).map(step => {
        try {
          step.offset = Number(step.offset)
        } catch (e) {
          step.offset = 0
        }

        step.params = {
          placement: step.params_placement,
          highlight: step.params_highlight,
          enableScrolling: true
        }
        delete step.params_placement
        delete step.params_highlight
        return step
      })

      return result
    }
  },
  methods: {
    startTour() {
      if (this.$refs.finTour.isRunning) {
        this.$tours[this.tourID]?.stop()
      }
      this.$nextTick(() => {
        this.$emit("startTour")
        this.$tours[this.tourID].start()
        // The tour can be started at a specific step whose index can be passed to the start-function
        // this.$tours[this.tourID].start(stepToStart)
      })
    },
    stopTour() {
      this.$tours[this.tourID]?.stop()
      this.$emit("stopTour")
    },
    finishTour() {
      this.$tours[this.tourID].finish()
      this.$emit("finishTour")
    },
    resize() {
      const currentWidth = window.innerWidth
      if (currentWidth !== this.windowWidth) {
        this.$nextTick(() => {
          if (this.$refs.finTour.isRunning) {
            this.startTour()
          }
        })
        this.windowWidth = currentWidth
      }
    },
    wrapInSlice(content) {
      return {
        primary: {
          text: content
        },
        type: 'Content'
      }
    },
    getAndExecuteJSStepFunction(jsArray) {
      if (jsArray != null && jsArray.length > 0) {
        const jsCode = new Function(jsArray[0].text)
        jsCode()
      }
    },
    trackAmplitudeEvent(event, from, to, info) {
      this.pushToAmplitude({
        'event': "fincraft_guided_tour_" + event,
        'step': (info ? info + ' ' : '') + (from !== undefined ? 'from step ' + from + ' ' : '') +
            (to !== undefined ? 'to step ' + to : ''),
        'step_choice': (from !== undefined ? 'from step ' + this.tourModel[from].target + ' ' : '') +
            (to !== undefined ? 'to step ' + this.tourModel[to].target : ''),
        'placement': this.tourName,
        'vue_breakpoint': this.$vuetify.breakpoint.name
      })
    },
    myCustomPreviousStepCallback(currentStep) {
      if (currentStep > 0) {
        this.getAndExecuteJSStepFunction(this.tourModel[currentStep - 1].js_before_step)
      }
      this.trackAmplitudeEvent('previous', currentStep, currentStep - 1, '')
    },
    // if currentStep === 1, it means that the custom nextStep Callback is called from step 2 to step 3
    myCustomNextStepCallback(currentStep) {
      if (this.tourModel.length > currentStep) {
        this.getAndExecuteJSStepFunction(this.tourModel[currentStep + 1].js_before_step)
      }
      this.trackAmplitudeEvent('next', currentStep === 0 ? 0 : currentStep, currentStep + 1, '')
    },
    myCustomStartCallback() {
      if (this.tourModel.length > 0) {
        this.getAndExecuteJSStepFunction(this.tourModel[0].js_before_step)
      }
    },

    myCustomStopCallback(currentStep) {
      // checking last event, because otherwise its sends both events finish and stop when the user finishes the tour
      if (!window.dataLayer[window.dataLayer.length - 1].event.includes("fincraft_guided_tour_end")) {
        this.trackAmplitudeEvent('end', undefined, undefined, 'stopping tour')
      }
    },
    myCustomFinishCallback(val) {
      this.trackAmplitudeEvent('end', undefined, undefined, 'finishing tour')
    }
  },
  watch: {
    $route() {
      this.stopTour()
    }
  },
  mounted() {
    // Watch currentStep and emit to parent
    this.$watch("$refs.finTour.currentStep",
        (newVal) => {
          this.$emit("stepChange", newVal)
        }
    )

    EventBus.$on("startTour", (id) => {
      if (id === this.tourID) {
        this.startTour()
      }
    })

    window.addEventListener("resize", this.resize)

    if (this.autoRun && this.steps && !this.tourWatched) {
      this.tourWatched = true
      this.startTour()
    }
  },
  destroyed() {
    EventBus.$off("startTour", this.startTour)
    window.removeEventListener("resize", this.resize)
  },
}
</script>


<style lang="scss">
/**
 * DO NOT SCOPE THIS CSS!
 */

.fin-tour {
  font-size: 1.1em;

  .v-step {
    &,
    &[data-popper-placement='top'] .v-step__arrow--dark::before,
    &[data-popper-placement='left'] .v-step__arrow--dark::before,
    &[data-popper-placement='right'] .v-step__arrow--dark::before,
    .v-step__header,
    &[data-popper-placement='bottom'] .v-step__arrow--dark::before {
      z-index: 203 !important;
      background-color: #4a6cb7 !important;
    }
  }
}
</style>
